import type { ForqSupabaseClient } from './client';
import {
  validateServiceBillingProposal,
  type MarkServiceBillingProposalInvoicedInput,
  type ReturnServiceBillingProposalInput,
  type SaveServiceBillingProposalInput,
  type ServiceBillingGateway,
  type ServiceBillingProposal,
  type ServiceBillingProposalStatus,
} from '@forq/service-billing-data';
import { SupabaseOperationError } from './errors';

const previewSchema = 'forq_next_preview';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function proposal(operation: string, value: unknown): ServiceBillingProposal {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'Die Abrechnungs-Prozedur lieferte keinen gültigen Vorschlag.');
  }
  return value as ServiceBillingProposal;
}

function proposals(operation: string, value: unknown): readonly ServiceBillingProposal[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'Die Abrechnungs-Prozedur lieferte keine gültige Vorschlagsliste.');
  }
  return value as readonly ServiceBillingProposal[];
}

/**
 * Service-Rechnungsvorschläge im PRODUKTIVBETRIEB -- Tabellen in `public`, Prozeduren in `forq`,
 * Mandant als uuid. Bis 09/2026 lief hier auch mit angeschlossener Datenbank der Demo-Gateway,
 * sodass jeder Vorschlag beim Neuladen verloren ging; siehe Migration
 * `20260904091000_service_billing_proposals.sql`.
 */
export class SupabaseServiceBillingGateway implements ServiceBillingGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async listProposals(
    tenantId: string,
    status: ServiceBillingProposalStatus | 'all' = 'all',
  ): Promise<readonly ServiceBillingProposal[]> {
    const { data, error } = await this.client.rpc('service_billing_list', {
      p_tenant_id: tenantId,
      p_status: status === 'all' ? null : status,
    });
    if (error) throw new SupabaseOperationError('serviceBilling.list', messageOf(error));
    return proposals('serviceBilling.list', data ?? []);
  }

  async saveAndSubmit(input: SaveServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    validateServiceBillingProposal(input);
    const { data, error } = await this.client.rpc('service_billing_submit', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('serviceBilling.submit', messageOf(error));
    return proposal('serviceBilling.submit', data);
  }

  async returnToService(input: ReturnServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    const { data, error } = await this.client.rpc('service_billing_return', {
      p_tenant_id: input.tenantId,
      p_proposal_id: input.proposalId,
      p_reason: input.reason.trim(),
    });
    if (error) throw new SupabaseOperationError('serviceBilling.return', messageOf(error));
    return proposal('serviceBilling.return', data);
  }

  async markInvoiced(input: MarkServiceBillingProposalInvoicedInput): Promise<ServiceBillingProposal> {
    const { data, error } = await this.client.rpc('service_billing_mark_invoiced', {
      p_tenant_id: input.tenantId,
      p_proposal_id: input.proposalId,
      p_invoice_number: input.invoiceNumber.trim(),
    });
    if (error) throw new SupabaseOperationError('serviceBilling.invoice', messageOf(error));
    return proposal('serviceBilling.invoice', data);
  }
}

/**
 * Dieselbe Fachlogik gegen die GETRENNTE Vorschaudatenbank (Schema `forq_next_preview`, Mandant als
 * Kürzel statt uuid). Wird ausschließlich vom Vorschaubetrieb verwendet -- im Produktivbetrieb gibt
 * es dieses Schema nicht.
 */
export class PreviewSupabaseServiceBillingGateway implements ServiceBillingGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async listProposals(
    tenantId: string,
    status: ServiceBillingProposalStatus | 'all' = 'all',
  ): Promise<readonly ServiceBillingProposal[]> {
    const { data, error } = await this.client.schema(previewSchema).rpc('service_billing_list', {
      p_tenant_code: tenantId,
      p_status: status === 'all' ? null : status,
    });
    if (error) throw new SupabaseOperationError('preview.serviceBilling.list', messageOf(error));
    return proposals('preview.serviceBilling.list', data ?? []);
  }

  async saveAndSubmit(input: SaveServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    validateServiceBillingProposal(input);
    const { data, error } = await this.client.schema(previewSchema).rpc('service_billing_submit', {
      p_tenant_code: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('preview.serviceBilling.submit', messageOf(error));
    return proposal('preview.serviceBilling.submit', data);
  }

  async returnToService(input: ReturnServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    const { data, error } = await this.client.schema(previewSchema).rpc('service_billing_return', {
      p_tenant_code: input.tenantId,
      p_proposal_id: input.proposalId,
      p_reason: input.reason.trim(),
    });
    if (error) throw new SupabaseOperationError('preview.serviceBilling.return', messageOf(error));
    return proposal('preview.serviceBilling.return', data);
  }

  async markInvoiced(input: MarkServiceBillingProposalInvoicedInput): Promise<ServiceBillingProposal> {
    const { data, error } = await this.client.schema(previewSchema).rpc('service_billing_mark_invoiced', {
      p_tenant_code: input.tenantId,
      p_proposal_id: input.proposalId,
      p_invoice_number: input.invoiceNumber.trim(),
    });
    if (error) throw new SupabaseOperationError('preview.serviceBilling.invoice', messageOf(error));
    return proposal('preview.serviceBilling.invoice', data);
  }
}
